import React, { useState, useEffect } from 'react';
import { Modal as AntModal, InputNumber as AntInputNumber, Radio, Space, message, Divider } from 'antd';

/**
 * 여행객 리스트 일괄수정 모달
 * 선택된 여행객들의 상품가, 원가, 택스및유류를 일괄 수정
 */
const BulkEditTravelerModal = ({ open, onClose, selectedTravelers, onApply }) => {
  // 수정할 필드 선택
  const [editProductPrice, setEditProductPrice] = useState(false);
  const [editCostPrice, setEditCostPrice] = useState(false);
  const [editTaxFuel, setEditTaxFuel] = useState(false);
  const [statusAction, setStatusAction] = useState('none');
  const [cancelFeeValue, setCancelFeeValue] = useState(0);
  const [restoreProductPriceValue, setRestoreProductPriceValue] = useState(null);
  const [restoreTaxFuelValue, setRestoreTaxFuelValue] = useState(null);

  // 각 필드의 값
  const [productPriceValue, setProductPriceValue] = useState(0);
  const [costPriceValue, setCostPriceValue] = useState(0);
  const [taxFuelValue, setTaxFuelValue] = useState(0);

  // 적용 방식: 'overwrite'(덮어쓰기), 'add'(더하기), 'subtract'(빼기)
  const [productPriceMode, setProductPriceMode] = useState('overwrite');
  const [costPriceMode, setCostPriceMode] = useState('overwrite');
  const [taxFuelMode, setTaxFuelMode] = useState('overwrite');

  // 모달이 열릴 때 초기화
  useEffect(() => {
    if (open) {
      setEditProductPrice(false);
      setEditCostPrice(false);
      setEditTaxFuel(false);
      setProductPriceValue(0);
      setCostPriceValue(0);
      setTaxFuelValue(0);
      setProductPriceMode('overwrite');
      setCostPriceMode('overwrite');
      setTaxFuelMode('overwrite');
      setStatusAction('none');
      setCancelFeeValue(0);
      setRestoreProductPriceValue(null);
      setRestoreTaxFuelValue(null);
    }
  }, [open]);

  const handleApply = () => {
    // 최소 하나의 필드 또는 상태 변경이 선택되어야 함
    if (!editProductPrice && !editCostPrice && !editTaxFuel && statusAction === 'none') {
      message.warning('수정할 항목 또는 상태 변경을 최소 하나 이상 선택해주세요.');
      return;
    }

    // 선택된 필드의 값이 유효한지 확인
    if (editProductPrice && productPriceValue < 0) {
      message.warning('상품가는 0 이상이어야 합니다.');
      return;
    }
    if (editCostPrice && costPriceValue < 0) {
      message.warning('원가는 0 이상이어야 합니다.');
      return;
    }
    if (editTaxFuel && taxFuelValue == null) {
      message.warning('택스 및 유류 금액을 입력해주세요.');
      return;
    }
    if (statusAction === 'cancel' && cancelFeeValue < 0) {
      message.warning('취소료는 0 이상이어야 합니다.');
      return;
    }
    if (statusAction === 'restore') {
      if (restoreProductPriceValue != null && restoreProductPriceValue < 0) {
        message.warning('복원 시 상품가는 0 이상이어야 합니다.');
        return;
      }
      if (restoreTaxFuelValue != null && restoreTaxFuelValue < 0) {
        message.warning('복원 시 텍스 및 유류는 0 이상이어야 합니다.');
        return;
      }
    }

    // 적용할 변경사항 구성
    let productPriceChange = editProductPrice ? { value: productPriceValue, mode: productPriceMode } : null;
    let taxFuelChange = editTaxFuel ? { value: taxFuelValue, mode: taxFuelMode } : null;

    // 상태가 취소 해제일 때, 별도의 복원 금액이 입력되어 있고 일반 가격/택스 수정 옵션을 사용하지 않은 경우
    if (statusAction === 'restore') {
      if (!editProductPrice && restoreProductPriceValue != null) {
        productPriceChange = { value: restoreProductPriceValue, mode: 'overwrite' };
      }
      if (!editTaxFuel && restoreTaxFuelValue != null) {
        taxFuelChange = { value: restoreTaxFuelValue, mode: 'overwrite' };
      }
    }

    const changes = {
      productPrice: productPriceChange,
      costPrice: editCostPrice ? { value: costPriceValue, mode: costPriceMode } : null,
      taxFuel: taxFuelChange,
      statusAction: statusAction !== 'none' ? statusAction : null,
      cancelFee: statusAction === 'cancel' ? { value: cancelFeeValue } : null,
    };

    onApply(changes);
    onClose();
  };

  const formatNumber = (val) => {
    if (typeof val === 'number') return val.toLocaleString('ko-KR');
    const s = String(val || '');
    return s.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const parseNumber = (val) => (val ? val.toString().replace(/,/g, '') : '');

  return (
    <AntModal
      open={open}
      onCancel={onClose}
      onOk={handleApply}
      title="여행객 일괄수정"
      okText="적용"
      cancelText="취소"
      width={600}
      centered
      maskClosable={false}
    >
      <div className="space-y-4">
        <div className="text-sm text-gray-600 bg-blue-50 border border-blue-200 rounded p-3">
          <div className="font-medium text-blue-800 mb-1">
            선택된 여행객: {selectedTravelers?.length || 0}명
          </div>
          <div className="text-xs text-blue-800 flex flex-wrap gap-1">
            {(selectedTravelers || []).length === 0 ? (
              <span>선택된 여행객이 없습니다.</span>
            ) : (
              (selectedTravelers || []).map((t, idx) => (
                <span key={idx} className="px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded border border-blue-200">
                  {t?.한글이름 || '(이름 없음)'}
                </span>
              ))
            )}
          </div>
        </div>

        <Divider className="my-3" />

        <div className="border border-gray-200 rounded p-4 bg-white">
          <div className="text-xs font-medium text-gray-800 mb-2">상태 일괄 변경</div>
          <div className="text-[11px] text-gray-500 mb-3">
            선택한 여행객의 상태를 한 번에 변경할 수 있습니다. 완전 삭제는 즉시 삭제 처리됩니다.
          </div>
          <Radio.Group
            size="small"
            value={statusAction}
            onChange={(e) => setStatusAction(e.target.value)}
          >
            <Space direction="horizontal">
              <Radio value="none">변경 없음</Radio>
              <Radio value="cancel">취소 처리</Radio>
              <Radio value="restore">취소 해제</Radio>
              <Radio value="delete">완전 삭제</Radio>
            </Space>
          </Radio.Group>
          {statusAction === 'cancel' && (
            <div className="mt-3 ml-1 space-y-1">
              <div className="text-[11px] text-gray-600">취소 처리 시 적용할 취소료 (여행객 1인 기준)</div>
              <AntInputNumber
                size="small"
                min={0}
                value={cancelFeeValue}
                onChange={(v) => setCancelFeeValue(Number(v) || 0)}
                style={{ width: '100%' }}
                formatter={formatNumber}
                parser={parseNumber}
                placeholder="취소료 금액 입력"
              />
              <div className="text-[11px] text-gray-500">※ 입력한 취소료는 선택된 여행객 각각의 상품금액(취소료)으로 설정되며, 택스 및 유류는 0으로 처리됩니다.</div>
            </div>
          )}
          {statusAction === 'restore' && (
            <div className="mt-3 ml-1 space-y-2">
              <div className="text-[11px] text-gray-600">
                취소 해제 시 적용할 상품가 및 텍스/유류 (여행객 1인 기준, 비워두면 금액은 변경하지 않습니다)
              </div>
              <div className="grid grid-cols-1 gap-2">
                <div className="flex items-center gap-2">
                  <span className="w-20 text-[11px] text-gray-600">상품가</span>
                  <AntInputNumber
                    size="small"
                    min={0}
                    value={restoreProductPriceValue}
                    onChange={(v) => setRestoreProductPriceValue(v == null ? null : Number(v) || 0)}
                    style={{ width: '100%' }}
                    formatter={formatNumber}
                    parser={parseNumber}
                    placeholder="변경하지 않으려면 비워두세요"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-20 text-[11px] text-gray-600">텍스 및 유류</span>
                  <AntInputNumber
                    size="small"
                    min={0}
                    value={restoreTaxFuelValue}
                    onChange={(v) => setRestoreTaxFuelValue(v == null ? null : Number(v) || 0)}
                    style={{ width: '100%' }}
                    formatter={formatNumber}
                    parser={parseNumber}
                    placeholder="변경하지 않으려면 비워두세요"
                  />
                </div>
              </div>
              
            </div>
          )}
        </div>

        {/* 상품가 */}
        <div className="border border-gray-200 rounded p-4 bg-gray-50">
          <div className="flex items-center gap-2 mb-3">
            <input
              type="checkbox"
              id="edit-product-price"
              checked={editProductPrice}
              onChange={(e) => setEditProductPrice(e.target.checked)}
              className="w-4 h-4"
            />
            <label htmlFor="edit-product-price" className="font-medium text-gray-800 cursor-pointer">
              상품가 수정
            </label>
          </div>
          {editProductPrice && (
            <div className="ml-6 space-y-3">
              <div>
                <div className="text-xs text-gray-600 mb-1">적용 방식</div>
                <Radio.Group
                  value={productPriceMode}
                  onChange={(e) => setProductPriceMode(e.target.value)}
                  size="small"
                >
                  <Space direction="horizontal">
                    <Radio value="overwrite">덮어쓰기</Radio>
                    <Radio value="add">인상</Radio>
                    <Radio value="subtract">인하</Radio>
                  </Space>
                </Radio.Group>
              </div>
              <div>
                <div className="text-xs text-gray-600 mb-1">
                  {productPriceMode === 'overwrite' ? '새 상품가' : productPriceMode === 'add' ? '더할 금액' : '뺄 금액'}
                </div>
                <AntInputNumber
                  size="middle"
                  min={0}
                  value={productPriceValue}
                  onChange={(v) => setProductPriceValue(Number(v) || 0)}
                  style={{ width: '100%' }}
                  formatter={formatNumber}
                  parser={parseNumber}
                  placeholder="금액 입력"
                />
              </div>
            </div>
          )}
        </div>

        {/* 원가 */}
        {false && (
          <div className="border border-gray-200 rounded p-4 bg-gray-50">
            <div className="flex items-center gap-2 mb-3">
              <input
                type="checkbox"
                id="edit-cost-price"
                checked={editCostPrice}
                onChange={(e) => setEditCostPrice(e.target.checked)}
                className="w-4 h-4"
              />
              <label htmlFor="edit-cost-price" className="font-medium text-gray-800 cursor-pointer">
                원가 수정
              </label>
            </div>
            {editCostPrice && (
              <div className="ml-6 space-y-3">
                <div>
                  <div className="text-xs text-gray-600 mb-1">적용 방식</div>
                  <Radio.Group
                    value={costPriceMode}
                    onChange={(e) => setCostPriceMode(e.target.value)}
                    size="small"
                  >
                    <Space direction="horizontal">
                      <Radio value="overwrite">덮어쓰기</Radio>
                      <Radio value="add">인상</Radio>
                      <Radio value="subtract">인하</Radio>
                    </Space>
                  </Radio.Group>
                </div>
                <div>
                  <div className="text-xs text-gray-600 mb-1">
                    {costPriceMode === 'overwrite' ? '새 원가' : costPriceMode === 'add' ? '더할 금액' : '뺄 금액'}
                  </div>
                  <AntInputNumber
                    size="middle"
                    min={0}
                    value={costPriceValue}
                    onChange={(v) => setCostPriceValue(Number(v) || 0)}
                    style={{ width: '100%' }}
                    formatter={formatNumber}
                    parser={parseNumber}
                    placeholder="금액 입력"
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* 택스 및 유류 */}
        <div className="border border-gray-200 rounded p-4 bg-gray-50">
          <div className="flex items-center gap-2 mb-3">
            <input
              type="checkbox"
              id="edit-tax-fuel"
              checked={editTaxFuel}
              onChange={(e) => setEditTaxFuel(e.target.checked)}
              className="w-4 h-4"
            />
            <label htmlFor="edit-tax-fuel" className="font-medium text-gray-800 cursor-pointer">
              택스 및 유류 수정
            </label>
          </div>
          {editTaxFuel && (
            <div className="ml-6 space-y-3">
              <div>
                <div className="text-xs text-gray-600 mb-1">적용 방식</div>
                <Radio.Group
                  value={taxFuelMode}
                  onChange={(e) => setTaxFuelMode(e.target.value)}
                  size="small"
                >
                  <Space direction="horizontal">
                    <Radio value="overwrite">덮어쓰기</Radio>
                    <Radio value="add">인상</Radio>
                    <Radio value="subtract">인하</Radio>
                  </Space>
                </Radio.Group>
              </div>
              <div>
                <div className="text-xs text-gray-600 mb-1">
                  {taxFuelMode === 'overwrite' ? '새 택스 및 유류' : taxFuelMode === 'add' ? '더할 금액' : '뺄 금액'}
                </div>
                <AntInputNumber
                  size="middle"
                  min={0}
                  value={taxFuelValue}
                  onChange={(v) => setTaxFuelValue(Number(v) || 0)}
                  style={{ width: '100%' }}
                  formatter={formatNumber}
                  parser={parseNumber}
                  placeholder="금액 입력"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </AntModal>
  );
};

export default BulkEditTravelerModal;
